import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import RefreshBar from '@/components/ui/RefreshBar'
import TitleRow from '@/components/ui/TitleRow'
import { useLastClose } from '@/features/market/lib/useLastClose'
import { sectorsKeys } from '@/features/sectors/api/keys'
import { useSectors } from '@/features/sectors/api/queries'
import { buildShareGroups } from '@/features/sectors/lib/shareGroups'
import SectorsTable from '@/features/sectors/components/SectorsTable'
import TurnoverShareCard from '@/features/sectors/components/TurnoverShareCard'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import { SectorsBoardBody } from '@/screens/market/components/SectorsBoardSkeleton'
import { loadSectors, sectorsSearchSchema } from '@/screens/market/SectorsScreen.loader'
import type { SectorsOverview } from '@/schemas/sectors'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title="Sectors" />

/**
 * The whole tab is one query keyed on the view chip (see the loader), so —
 * same reasoning as Market · Overview — the first load draws one page-wide
 * skeleton rather than a skeleton per card.
 */
function SectorsPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading the sectors…" header={HEADER} pageTitle="Sectors">
      <SectorsBoardBody />
    </ScreenLayout>
  )
}

/**
 * The copy the page used for a failed load before it had a loader —
 * `EmptyState kind="failed" title="Couldn't load sectors"` — carried
 * forward onto the route's own error page.
 */
function SectorsError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Couldn't load sectors" />
}

export const Route = createFileRoute('/_app/market/sectors')({
  validateSearch: sectorsSearchSchema,
  loader: loadSectors,
  pendingComponent: SectorsPending,
  errorComponent: SectorsError,
  component: SectorsScreen,
})

/**
 * Market · Sectors — the whole tab read once, as `useSectors` keyed on `view`,
 * which now only picks the table's opening sort (the 2026-09-28 refresh drops
 * the ordering tabs for column sorts). The route's loader has awaited it before
 * this mounts, so the body branches only for a later failure or for the
 * offline state, which offers the last board this session saw (`5327:4326`).
 *
 * The turnover ring and the table share two pieces of state, both owned here: the group a pointer
 * has lit (`hover`) and the one the member clicked to hold (`held`).
 */
function SectorsScreen() {
  const { view } = Route.useSearch()
  const online = useOnline()

  const [hover, setHover] = useState<string | null>(null)
  const [held, setHeld] = useState<string | null>(null)

  const sectors = useSectors(view)
  const lastClose = useLastClose<SectorsOverview>(sectorsKeys.overviews(), sectors.data)
  const data = lastClose.data
  const shares = useMemo(
    () => (data === undefined ? undefined : buildShareGroups(data.share)),
    [data],
  )
  const viewState = lastClose.isShowingSaved ? 'loaded' : resolveViewState(sectors, { online })

  return (
    <ScreenLayout header={HEADER} pageTitle="Sectors">
      {lastClose.isShowingSaved ? (
        <RefreshBar state="offline" onRetry={() => void sectors.refetch()} />
      ) : null}

      {viewState === 'failed' || viewState === 'offline' ? (
        <EmptyState
          kind={viewState}
          size="page"
          title={viewState === 'offline' ? undefined : "Couldn't load sectors"}
          onRetry={() => void sectors.refetch()}
          retrying={sectors.isFetching}
          meta={viewState === 'failed' ? lastUpdatedMeta(sectors.dataUpdatedAt) : undefined}
          savedAt={lastClose.savedAt}
          onShowLastClose={lastClose.showLastClose}
        />
      ) : data === undefined || shares === undefined ? (
        // Unreachable in practice: the loader has resolved the entry view
        // before this mounts, and `keepPreviousData` covers a later key.
        // `useQuery`'s type cannot see that, so the loaded shape stands in.
        <SectorsBoardBody />
      ) : (
        <>
          <TurnoverShareCard
            share={data.share}
            shares={shares}
            hover={hover}
            onHover={setHover}
            held={held}
            onHold={setHeld}
          />
          <SectorsTable
            sectors={data.cards}
            view={view}
            groupIdOf={shares.groupIdOf}
            hover={hover}
            onHover={setHover}
            held={held}
          />
        </>
      )}
    </ScreenLayout>
  )
}
